<script>
/**
 * Standalone host for the levelling comparison (EvaluationCompare / Compare.vue)
 * with NO current document — opened from the jobs grid via the Atlas flyout's
 * "Compare levels" action.
 *
 * EvaluationCompare normally receives its framework props (functions / tracks /
 * levels / factors) from EvaluationHolder, which loads them as a side effect of
 * loading a document's evaluation. Here there is no document, so we load the
 * client framework directly: POST /document/loadDocumentLfEvaluation with no
 * doc_id returns only the framework (document/response come back null). We then
 * feed EvaluationCompare in `standalone` mode with `doc: null`; the user builds
 * the comparison by picking jobs via the hierarchy picker inside it.
 */
import axios from 'axios';
import { defineComponent, ref, computed, onMounted } from 'vue';
import EvaluationCompare from './Compare.vue';

export default defineComponent({
    name: 'LevelCompareStandalone',
    components: { EvaluationCompare },
    props: {
        // Jobs to seed the comparison with — the doc_ids Atlas identified.
        docIds: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['openDocument', 'copyDocument'],
    setup() {
        const loading = ref(true);
        const error = ref(null);
        const framework = ref(null);

        // Mirror EvaluationHolder's mapping so EvaluationCompare gets identical
        // prop shapes. classificationFactors = the weighted factors used by the
        // breakdown tab.
        const functions = computed(() => framework.value?.functions ?? []);
        const tracks = computed(() => framework.value?.tracks ?? []);
        const levels = computed(() => framework.value?.levels ?? []);
        const classificationFactors = computed(() =>
            (framework.value?.factors ?? []).filter((f) => f.weighting > 0),
        );

        onMounted(async () => {
            try {
                const resp = await axios.post('document/loadDocumentLfEvaluation', {
                    doc_id: null,
                    doc_reference: null,
                    document_lf_evaluation_id: null,
                });
                if (resp.data.Status === 'OK') {
                    framework.value = resp.data.Data?.evaluation ?? null;
                    if (!framework.value) {
                        error.value = 'No levelling framework is configured for this client.';
                    }
                } else {
                    error.value = resp.data.Message || 'Unable to load the levelling framework.';
                }
            } catch (err) {
                console.error('[LevelCompareStandalone] framework load failed', err);
                error.value = 'Unable to load the levelling framework.';
            } finally {
                loading.value = false;
            }
        });

        return { loading, error, functions, tracks, levels, classificationFactors };
    },
});
</script>

<template>
    <div class="level-compare-standalone">
        <div v-if="loading" class="level-compare-standalone__loading">
            <v-progress-circular indeterminate color="primary" size="64" />
            <p class="mt-4 text-h6">Loading levelling framework…</p>
        </div>

        <v-alert v-else-if="error" type="error" text dense class="ma-4">
            {{ error }}
        </v-alert>

        <EvaluationCompare
            v-else
            standalone
            :doc="null"
            :doc-ids="docIds"
            :functions="functions"
            :tracks="tracks"
            :factors="classificationFactors"
            :levels="levels"
            @openDocument="$emit('openDocument', $event)"
            @copyDocument="$emit('copyDocument', $event)"
            @close="$emit('close')"
        />
    </div>
</template>

<style scoped>
.level-compare-standalone {
    height: 100%;
}
.level-compare-standalone__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 16px;
    text-align: center;
}
</style>
